import { useEffect, useMemo, useState } from "react";
import { Users, Search, TrendingUp, Wallet, BadgeDollarSign, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function shortDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); }
}

const STATUS_STYLE = {
  Active:     { bg: "rgba(34,192,115,0.18)", bd: "rgba(34,192,115,0.5)",  fg: "#6ee7b7" },
  FTD:        { bg: "rgba(212,175,55,0.20)", bd: "rgba(212,175,55,0.55)", fg: "#fff7d6" },
  Registered: { bg: "rgba(148,163,184,0.16)",bd: "rgba(148,163,184,0.4)", fg: "#cbd5e1" },
};

export default function Referrals() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "joined_at", dir: "desc" });

  useEffect(() => {
    (async () => {
      try {
        // Pull legacy current-period stats + detailed lifetime view in parallel
        const [base, detailed] = await Promise.all([
          api.get("/aff-panel/referrals"),
          api.get("/aff-panel/referrals/detailed").catch(() => ({ data: { rows: [] } })),
        ]);
        const detMap = new Map(
          (detailed.data?.rows || []).map((d) => [d.username, d])
        );
        const merged = (base.data?.rows || []).map((r) => {
          const d = detMap.get(r.username) || {};
          return {
            ...r,
            first_deposit_amount: d.first_deposit_amount || 0,
            first_deposit_date: d.first_deposit_date || "",
            total_deposit: d.total_deposit ?? 0,
            user_status: d.status || (d.is_ftd ? "FTD" : "Registered"),
            is_ftd: !!d.is_ftd,
          };
        });
        // Also include detailed rows that may not be in base (e.g. outside current period)
        const baseUsers = new Set(merged.map((r) => r.username));
        (detailed.data?.rows || []).forEach((d) => {
          if (!baseUsers.has(d.username)) {
            merged.push({
              username: d.username, phone: d.phone, joined_at: d.register_date,
              deposits: 0, withdrawals: 0, balance: d.balance || 0, net_loss: 0, commission: 0,
              first_deposit_amount: d.first_deposit_amount || 0,
              first_deposit_date: d.first_deposit_date || "",
              total_deposit: d.total_deposit ?? 0,
              user_status: d.status || (d.is_ftd ? "FTD" : "Registered"),
              is_ftd: !!d.is_ftd,
            });
          }
        });
        setRows(merged);
      } catch (e) { toast.error("Failed to load referrals"); }
      finally { setLoading(false); }
    })();
  }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.deposits += Number(r.deposits || 0);
        acc.withdrawals += Number(r.withdrawals || 0);
        acc.net_loss += Number(r.net_loss || 0);
        acc.commission += Number(r.commission || 0);
        acc.ftd_count += r.is_ftd ? 1 : 0;
        acc.active_count += r.user_status === "Active" ? 1 : 0;
        return acc;
      },
      { deposits: 0, withdrawals: 0, net_loss: 0, commission: 0, ftd_count: 0, active_count: 0 }
    );
  }, [rows]);

  const filtered = useMemo(() => {
    let f = rows.filter((r) => {
      if (filter.trim() && !(r.username || "").toLowerCase().includes(filter.toLowerCase())) return false;
      if (statusFilter !== "all" && r.user_status !== statusFilter) return false;
      return true;
    });
    f = [...f].sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      const ak = a[sort.key]; const bk = b[sort.key];
      if (typeof ak === "number" || typeof bk === "number")
        return (Number(ak || 0) - Number(bk || 0)) * dir;
      return String(ak || "").localeCompare(String(bk || "")) * dir;
    });
    return f;
  }, [rows, filter, statusFilter, sort]);

  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div>
      <PageHeader
        title="My Players"
        eyebrow="Audience"
        subtitle={`${rows.length} player${rows.length === 1 ? "" : "s"} referred · ${totals.ftd_count} FTD · ${totals.active_count} Active`}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
        <StatCard label="Total Deposits" value={totals.deposits} prefix="৳ " decimals={2} accent="blue" icon={Wallet} delay={0} />
        <StatCard label="Total Withdrawals" value={totals.withdrawals} prefix="৳ " decimals={2} accent="rose" icon={Wallet} delay={60} />
        <StatCard label="Total Net Loss" value={totals.net_loss} prefix="৳ " decimals={2} accent="emerald" icon={TrendingUp} delay={120} />
        <StatCard label="Total Commission" value={totals.commission} prefix="৳ " decimals={2} accent="gold" icon={BadgeDollarSign} delay={180} />
      </div>

      <div className="p-glass p-fade-up" style={{ padding: "16px 18px" }}>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="p-input-shell" style={{ flex: 1, maxWidth: 360, minWidth: 220 }}>
            <Search size={14} className="text-white/40" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by player username" data-testid="referrals-search" />
          </div>
          <div className="flex items-center gap-1">
            {["all", "Active", "FTD", "Registered"].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className="h-9 px-3 rounded-md text-[11px] font-bold capitalize" style={{
                background: statusFilter === s ? "rgba(34,192,115,0.22)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${statusFilter === s ? "rgba(34,192,115,0.55)" : "rgba(255,255,255,0.10)"}`,
                color: statusFilter === s ? "#34d399" : "#cbd5e1",
              }} data-testid={`ref-status-${s}`}>{s === "all" ? "All" : s}</button>
            ))}
          </div>
          <div className="text-[11px] text-white/45 ml-auto">{filtered.length} shown</div>
        </div>

        {loading ? (
          <div className="py-10 text-center">
            <div className="p-skeleton mx-auto" style={{ width: 160, height: 14, marginBottom: 8 }} />
            <div className="p-skeleton mx-auto" style={{ width: 120, height: 12 }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-3" style={{
              background: "linear-gradient(135deg, rgba(34, 192, 115, 0.18), rgba(34, 192, 115, 0.02))",
              border: "1px solid rgba(34, 192, 115, 0.4)",
            }}>
              <Users size={26} className="text-emerald-300" />
            </div>
            <div className="text-white/65 text-sm font-semibold">No players yet</div>
            <div className="text-white/45 text-[12px] mt-1">Share your promo code to grow your network</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="p-table" data-testid="referrals-table">
              <thead>
                <tr>
                  <Sh k="username" onSort={onSort}>User</Sh>
                  <Sh k="joined_at" onSort={onSort}>Joined</Sh>
                  <Sh k="first_deposit_amount" align="right" onSort={onSort}>First Deposit</Sh>
                  <Sh k="total_deposit" align="right" onSort={onSort}>Total Deposit</Sh>
                  <Sh k="balance" align="right" onSort={onSort}>Balance</Sh>
                  <Sh k="net_loss" align="right" onSort={onSort}>Net Loss</Sh>
                  <Sh k="commission" align="right" onSort={onSort}>Commission</Sh>
                  <Sh k="user_status" align="center" onSort={onSort}>Status</Sh>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const stStyle = STATUS_STYLE[r.user_status] || STATUS_STYLE.Registered;
                  return (
                    <tr key={i}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10.5px] font-black" style={{
                            background: "linear-gradient(135deg, #fff7d6, #d4af37 35%, #22c073 100%)",
                            color: "#1a1206",
                          }}>{(r.username || "?").slice(0, 2).toUpperCase()}</div>
                          <div>
                            <div className="font-bold">{r.username}</div>
                            <div className="text-[10.5px] text-white/45 font-mono">{r.phone || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-white/70">{shortDate(r.joined_at)}</td>
                      <td className="text-right">
                        {r.first_deposit_amount > 0 ? (
                          <div>
                            <div className="font-bold" style={{ color: "#d4af37" }}>৳ {fmt(r.first_deposit_amount)}</div>
                            <div className="text-[10.5px] text-white/45">{shortDate(r.first_deposit_date)}</div>
                          </div>
                        ) : <span className="text-white/35">—</span>}
                      </td>
                      <td className="text-right font-semibold">৳ {fmt(r.total_deposit ?? r.deposits)}</td>
                      <td className="text-right text-white/70">৳ {fmt(r.balance)}</td>
                      <td className="text-right font-semibold">৳ {fmt(r.net_loss)}</td>
                      <td className="text-right"><span className="p-gold-text font-black">৳ {fmt(r.commission)}</span></td>
                      <td className="text-center">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{
                          background: stStyle.bg, border: `1px solid ${stStyle.bd}`, color: stStyle.fg,
                        }}>{r.user_status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Sh({ k, children, align = "left", onSort }) {
  return (
    <th onClick={() => onSort(k)} className="cursor-pointer select-none" style={{ textAlign: align }}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowDownUp size={10} className="opacity-50" />
      </span>
    </th>
  );
}

